import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createResource, useResource, type RouteResource } from '@atlassian/react-resource-router';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	JSM_PORTAL_WIDGET_USER_PROPERTY,
	RESOURCE_TYPE_SERVICEDESK_PORTAL_WIDGET_DISMISSED,
} from './constants.tsx';
import type { WidgetDismissedState } from './types.tsx';

const getDataLoader = () =>
	import(
		/* webpackChunkName: "async-resource-servicedesk-portal-widget-dismissed",
		jiraSpaEntry: "async-resource-servicedesk-portal-widget-dismissed" */
		'./get-widget-dismissed-state'
	);

export const widgetDismissedStateResource: RouteResource<WidgetDismissedState> =
	createResource<WidgetDismissedState>({
		type: RESOURCE_TYPE_SERVICEDESK_PORTAL_WIDGET_DISMISSED,
		getKey: () => RESOURCE_TYPE_SERVICEDESK_PORTAL_WIDGET_DISMISSED,
		getDataLoader,
		maxAge: Infinity,
	});

export const useServiceDeskPortalWidgetResource = (
	accountId: AccountId | null,
): {
	loading: boolean;
	widgetIsDismissed: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dismissAnnouncement: () => Promise<any> | undefined;
} => {
	const { data, loading, error, refresh, update } = useResource(widgetDismissedStateResource);
	const loadingRef = useRef(false);
	const errorRef = useRef<unknown>();
	const [userPropertiesError, setUserPropertiesError] = useState<Error>();
	errorRef.current = userPropertiesError || error;

	const dismissAnnouncement = useCallback(() => {
		update(() => ({ widgetIsDismissed: true }));

		if (accountId) {
			if (loadingRef.current) {
				const err = new Error('User properties is loading');
				fireErrorAnalytics({
					meta: {
						id: 'useServiceDeskPortalWidgetResource',
						packageName: 'jiraRouterResourcesServicedeskPortalWidgetResources',
						teamName: 'Seqouia',
					},
					error: err,
					sendToPrivacyUnsafeSplunk: true,
				});
				setUserPropertiesError(err);
				return Promise.reject(err);
			}

			loadingRef.current = true;
			return setUserProperties(accountId, JSM_PORTAL_WIDGET_USER_PROPERTY, 'true')
				.catch((err) => {
					setUserPropertiesError(new Error('Error occured while setting user properties'));
					fireErrorAnalytics({
						meta: {
							id: 'useServiceDeskPortalWidgetResource',
							packageName: 'jiraRouterResourcesServicedeskPortalWidgetResources',
							teamName: 'Sequoia',
						},
						error: err,
						sendToPrivacyUnsafeSplunk: true,
					});
				})
				.finally(() => {
					loadingRef.current = false;
				});
		}
	}, [update, accountId]);

	useEffect(() => {
		if (!data && !loading && !error) {
			refresh();
		}
	}, [data, loading, refresh, error]);

	return useMemo(() => {
		if (errorRef.current || loading || !data || !accountId) {
			return {
				loading,
				widgetIsDismissed: true,
				dismissAnnouncement,
			};
		}

		return {
			widgetIsDismissed: data.widgetIsDismissed,
			loading,
			dismissAnnouncement,
		};
	}, [loading, data, accountId, dismissAnnouncement]);
};
